import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { groupsApi } from '@/api/groups';
import { type SerializedUser } from '@/api/users';
import { useUser } from './useUser';

const MIN_GROUPS_FOR_PICKER = 2;

export type GroupPickerKind = 'patient' | 'caregiver';

const COPY: Record<GroupPickerKind, { noun: string; nounWithArticle: string }> = {
  patient: { noun: 'paciente', nounWithArticle: 'este paciente' },
  caregiver: { noun: 'profesional', nounWithArticle: 'este profesional' },
};

interface PickerVisibility {
  mustPickGroup: boolean;
  showGroupPicker: boolean;
}

function userMustPickGroup(user: SerializedUser): boolean {
  const count = user.groupIds?.length ?? 0;
  return !!user.grouped && count >= MIN_GROUPS_FOR_PICKER;
}

function userMayPickGroup(user: SerializedUser): boolean {
  return !!user.companyGroupsEnabled && !!user.canManageGroups;
}

function computeVisibility(mode: 'new' | 'edit', user: SerializedUser | undefined): PickerVisibility {
  if (!user) return { mustPickGroup: false, showGroupPicker: false };

  if (mode === 'edit') {
    // Edit-mode picker is manager-only: coordinators can't relocate members
    // across groups via the patient/caregiver record. Coordinators see the
    // memberships read-only on the profile page instead.
    return { mustPickGroup: false, showGroupPicker: userMayPickGroup(user) };
  }

  const mustPickGroup = userMustPickGroup(user);
  return { mustPickGroup, showGroupPicker: mustPickGroup || userMayPickGroup(user) };
}

function buildHint(kind: GroupPickerKind, coordinatorPick: boolean, required: boolean): string {
  const { noun, nounWithArticle } = COPY[kind];
  if (coordinatorPick) {
    return `Selecciona al menos un grupo del que formas parte. El ${noun} ` +
      'sólo será visible para los usuarios asignados a esos grupos.';
  }
  if (required) {
    return `Selecciona al menos un grupo. El ${noun} será visible para los usuarios ` +
      'asignados a los grupos seleccionados.';
  }

  return `Sin grupo seleccionado, ${nounWithArticle} solo será visible para administradores.`;
}

export function useGroupMembershipPicker(
  mode: 'new' | 'edit', kind: GroupPickerKind, initialGroupIds: number[] = [],
) {
  const { user } = useUser();
  const { mustPickGroup: coordinatorMustPick, showGroupPicker } = computeVisibility(mode, user?.value);

  const selectedGroupIds = ref<number[]>([...initialGroupIds]);
  const groupPickError = ref<string | null>(null);

  const { data: creatorGroups } = useQuery({
    queryKey: ['groups', 'forCreatorPicker', kind],
    queryFn: () => groupsApi.indexForFilters(),
    initialData: [],
    enabled: showGroupPicker,
  });
  const groupItems = computed(() => creatorGroups.value.map(g => ({ value: g.id, title: g.name })));

  // Whoever sees the picker must leave the record in a group, as long as the
  // company has any group to pick (the server enforces the same rule).
  const mustPickGroup = computed(() =>
    coordinatorMustPick || (showGroupPicker && groupItems.value.length > 0),
  );
  const groupPickerHint = computed(() => buildHint(kind, coordinatorMustPick, mustPickGroup.value));

  function validate(): boolean {
    if (mustPickGroup.value && selectedGroupIds.value.length === 0) {
      groupPickError.value = 'Selecciona al menos un grupo.';
      return false;
    }
    groupPickError.value = null;
    return true;
  }

  return {
    showGroupPicker,
    mustPickGroup,
    selectedGroupIds,
    groupPickError,
    groupPickerHint,
    groupItems,
    validate,
  };
}
